import React from 'react';
import Link from "@mui/material/Link";
import {
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PDFViewer from "../../../components/PDFViewer";
import DataTableStandard from "../../../components/DataTableStandard";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { dateFormat, textfieldDateFormat } from "../../../utils/dateFunctions";
import { numberFormat } from "../../../utils/numberFunctions";
import { reverseSort } from '../../../utils/sortFunctions';

const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }

    return `$${numberFormat(Number(value).toFixed(2))}`;
};

const formatLineText = (code, description) => {
    if (code && description) {
        return `${code} - ${description}`;
    }

    return code || description || '';
};

const getEmlUri = (pdfUri) => {
    if (!pdfUri || typeof pdfUri !== 'string') {
        return '';
    }

    const emlUri = pdfUri.replace(/\.pdf(?=([?#]|$))/i, '.eml');

    return emlUri !== pdfUri ? emlUri : '';
};

const ReleasedBillsTable = (props) => {
    const [loading, setLoading] = React.useState(false);
    const [releasedBills, setReleasedBills] = React.useState([]);
    const [expandedRows, setExpandedRows] = React.useState(new Set());

    const toggleExpandedRow = React.useCallback((dataIndex) => {
        const row = releasedBills[dataIndex];
        const key = row?.billId ?? dataIndex;

        setExpandedRows((current) => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, [releasedBills]);

    React.useEffect(() => {
        const abortController = new AbortController();
        let active = true;

        const loadReleasedBills = async () => {
            setLoading(true);

            try {
                const data = await getAPIDataParamsReturn('/APBills/GetReleased', {
                    startDate: textfieldDateFormat(props.startDate),
                    endDate: textfieldDateFormat(props.endDate),
                }, abortController);

                if (!active) {
                    return;
                }

                setReleasedBills(Array.isArray(data) ? data : []);
                setExpandedRows(new Set());
            } catch (error) {
                if (active) {
                    setReleasedBills([]);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadReleasedBills();

        return () => {
            active = false;
            abortController.abort();
        };
    }, [props.endDate, props.reload, props.startDate]);

    const columns = React.useMemo(() => {
        const tenant = process.env.REACT_APP_MYOB_TENANT ? process.env.REACT_APP_MYOB_TENANT : 'https://aoc.myobadvanced.com/Main?';

        return [
            {
                name: 'myobid',
                label: 'MYOBid',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        const myobid = releasedBills[dataIndex]?.myobid;
                        if (!myobid) {
                            return <span></span>;
                        }

                        return <Link href={tenant + 'ScreenId=AP301000&Type=Bill&RefNbr=' + myobid} target='_blank'>{myobid}</Link>;
                    },
                    sortCompare: reverseSort,
                }
            },
            {
                name: 'status',
                label: 'Bill Status',
            },
            {
                name: 'supplierName',
                label: 'Supplier Name',
            },
            {
                name: 'supplierRef',
                label: 'Supplier Ref',
            },
            {
                name: 'date',
                label: 'Date',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        return <span>{releasedBills[dataIndex]?.date ? dateFormat(releasedBills[dataIndex].date) : ''}</span>;
                    },
                    sortCompare: reverseSort,
                }
            },
            {
                name: 'dueDate',
                label: 'Due Date',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        return <span>{releasedBills[dataIndex]?.dueDate ? dateFormat(releasedBills[dataIndex].dueDate) : ''}</span>;
                    },
                    sortCompare: reverseSort,
                }
            },
            {
                name: 'amount',
                label: 'Amount',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        return <span>{formatCurrency(releasedBills[dataIndex]?.amount)}</span>;
                    },
                    sortCompare: reverseSort,
                }
            },
            {
                name: 'balance',
                label: 'Balance',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        return <span>{formatCurrency(releasedBills[dataIndex]?.balance)}</span>;
                    },
                    sortCompare: reverseSort,
                }
            },
            {
                name: 'approvedBy',
                label: 'Approved By',
            },
            {
                name: 'emlDownload',
                label: 'EML',
                options: {
                    customBodyRenderLite: (dataIndex) => {
                        const emlUri = getEmlUri(releasedBills[dataIndex]?.aiBillUri);

                        if (!emlUri) {
                            return <span></span>;
                        }

                        return (
                            <IconButton
                                component="a"
                                href={emlUri}
                                download
                                size="small"
                                title="Download EML"
                                aria-label="Download EML"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        );
                    },
                    filter: false,
                    sort: false,
                }
            },
            {
                name: 'description',
                label: 'Description',
            },
        ];
    }, [releasedBills]);

    const options = React.useMemo(() => ({
        filterType: 'dropdown',
        selectableRowsHideCheckboxes: true,
        fixedHeader: true,
        rowsPerPage: 15,
        responsive: 'standard',
        customRowRender: (data, dataIndex) => {
            const row = releasedBills[dataIndex];
            const rowKey = row?.billId ?? dataIndex;
            const isExpanded = expandedRows.has(rowKey);
            const cellStyle = {
                fontSize: '12px',
                paddingTop: '3px',
                paddingBottom: '3px',
                whiteSpace: 'nowrap',
                maxWidth: '180px',
                overflow: 'hidden',
            };

            return (
                <React.Fragment key={rowKey}>
                    <TableRow
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleExpandedRow(dataIndex)}
                    >
                        {columns.map((column, index) => (
                            <TableCell key={column.name} style={cellStyle}>
                                {typeof data[index] === 'function' ? data[index](dataIndex) : data[index]}
                            </TableCell>
                        ))}
                    </TableRow>
                    {isExpanded && (
                        <TableRow>
                            <TableCell colSpan={columns.length} sx={{ backgroundColor: 'background.default', p: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    <Box
                                        sx={{
                                            flex: '1 1 420px',
                                            minWidth: 0,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            bgcolor: 'background.paper'
                                        }}
                                    >
                                        {row?.aiBillUri ? (
                                            <PDFViewer URI={row.aiBillUri} height="72vh" />
                                        ) : (
                                            <Box sx={{ p: 2 }}>
                                                <Typography variant="body2">No PDF is available for this AIBill.</Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ flex: '1.35 1 560px', minWidth: 0 }}>
                                        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, mb: 3 }}>
                                            <DetailCard label="Bill MYOBid" value={row?.myobid} />
                                            <DetailCard label="Bill Status" value={row?.status} />
                                            <DetailCard label="Supplier" value={row?.supplierName} />
                                            <DetailCard label="Approved By" value={row?.approvedBy} />
                                            <DetailCard label="Date" value={row?.date ? dateFormat(row.date) : ''} />
                                            <DetailCard label="Due Date" value={row?.dueDate ? dateFormat(row.dueDate) : ''} />
                                            <DetailCard label="Amount" value={formatCurrency(row?.amount)} />
                                            <DetailCard label="Tax / Balance" value={`${formatCurrency(row?.tax)} / ${formatCurrency(row?.balance)}`} />
                                        </Box>

                                        <Typography variant="h6" sx={{ mb: 1 }}>Bill Lines</Typography>

                                        {row?.billLines?.length ? (
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Row</TableCell>
                                                        <TableCell>Description</TableCell>
                                                        <TableCell>Quantity</TableCell>
                                                        <TableCell>Unit Cost</TableCell>
                                                        <TableCell>Amount</TableCell>
                                                        <TableCell>Account</TableCell>
                                                        <TableCell>Sub Account</TableCell>
                                                        <TableCell>Tax</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {row.billLines.map((line) => (
                                                        <TableRow key={line.id ?? `${rowKey}-${line.rowNumber}`}>
                                                            <TableCell>{line.rowNumber}</TableCell>
                                                            <TableCell>{line.description}</TableCell>
                                                            <TableCell>{line.quantity}</TableCell>
                                                            <TableCell>{formatCurrency(line.unitCost)}</TableCell>
                                                            <TableCell>{formatCurrency(line.amount)}</TableCell>
                                                            <TableCell>{formatLineText(line.accountMyobId, line.accountDescription)}</TableCell>
                                                            <TableCell>{formatLineText(line.subAccountMyobId, line.subAccountDescription)}</TableCell>
                                                            <TableCell>{line.taxCategory}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">No bill lines found.</Typography>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Button size="small" variant="outlined" onClick={(event) => {
                                        event.stopPropagation();
                                        toggleExpandedRow(dataIndex);
                                    }}>
                                        Close
                                    </Button>
                                </Box>
                            </TableCell>
                        </TableRow>
                    )}
                </React.Fragment>
            );
        }
    }), [columns, expandedRows, releasedBills, toggleExpandedRow]);

    return (
        <DataTableStandard
            data={releasedBills}
            title={<Typography variant={'h3'}>Released Bills</Typography>}
            columns={columns}
            options={options}
            returnSearch={!loading}
            reload={props.reloadFunc}
            shrink={true}
        />
    );
};

const DetailCard = (props) => {
    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary">{props.label}</Typography>
            <Typography variant="body1">{props.value || ''}</Typography>
        </Box>
    );
};

export default ReleasedBillsTable;